"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type {
  DistrictSchoolLevelType,
  IPublishedMapLevelSummary,
} from "@/providers/store/api/find-my-school/find-my-school.interface";
import { resolveFindMySchoolLayerTypes } from "@/shared/utils/find-my-school-layer-types.util";

export interface FindMySchoolLayersContextValue {
  districtVisible: boolean;
  setDistrictVisible: (v: boolean) => void;
  schoolBoundariesHiddenTypes: string[];
  toggleSchoolBoundaryType: (type: string) => void;
  schoolPinsHiddenTypes: string[];
  toggleSchoolPinType: (type: string) => void;
  /** Levels that exist for this district (boundaries / toggles only apply to these). */
  districtSchoolLevelTypes: readonly DistrictSchoolLevelType[];
  /** Published map levels for this district (FMS toggles / Community gate). */
  publishedMapLevels: readonly IPublishedMapLevelSummary[];
  /** Map level ids currently hidden on the map (default: all published). */
  mapLevelsHiddenIds: string[];
  toggleMapLevelId: (id: string) => void;
}

const FindMySchoolLayersContext = createContext<FindMySchoolLayersContextValue | null>(null);

export function useFindMySchoolLayers() {
  const ctx = useContext(FindMySchoolLayersContext);
  if (!ctx) throw new Error("useFindMySchoolLayers must be used inside FindMySchoolLayersProvider");
  return ctx;
}

export interface FindMySchoolLayersProviderProps {
  children: ReactNode;
  hasDistrictLayer?: boolean;
  /** School levels that may show boundary layers and toggles. Missing/empty hides school layers. */
  districtSchoolLevelTypes?: readonly DistrictSchoolLevelType[];
  /** Published MapLevels eligible for FMS (ACTIVE + visibility). */
  publishedMapLevels?: readonly IPublishedMapLevelSummary[];
}

export function FindMySchoolLayersProvider({
  children,
  hasDistrictLayer = false,
  districtSchoolLevelTypes: districtSchoolLevelTypesProp,
  publishedMapLevels: publishedMapLevelsProp,
}: FindMySchoolLayersProviderProps) {
  const districtSchoolLevelTypes = useMemo<readonly DistrictSchoolLevelType[]>(
    () => resolveFindMySchoolLayerTypes(districtSchoolLevelTypesProp),
    [districtSchoolLevelTypesProp],
  );

  const publishedMapLevels = useMemo<readonly IPublishedMapLevelSummary[]>(
    () => publishedMapLevelsProp ?? [],
    [publishedMapLevelsProp],
  );

  const publishedMapLevelIdsKey = useMemo(
    () => publishedMapLevels.map((level) => level.id).join(","),
    [publishedMapLevels],
  );

  const [districtVisible, setDistrictVisible] = useState(true);
  const [schoolBoundariesHiddenTypes, setSchoolBoundariesHiddenTypes] = useState<string[]>(() => [
    ...districtSchoolLevelTypes,
  ]);
  const [schoolPinsHiddenTypes, setSchoolPinsHiddenTypes] = useState<string[]>([]);
  const [mapLevelsHiddenIds, setMapLevelsHiddenIds] = useState<string[]>(() =>
    publishedMapLevels.map((level) => level.id),
  );

  useEffect(() => {
    setSchoolBoundariesHiddenTypes([...districtSchoolLevelTypes] as string[]);
  }, [districtSchoolLevelTypes]);

  useEffect(() => {
    setMapLevelsHiddenIds(
      publishedMapLevelIdsKey.length > 0 ? publishedMapLevelIdsKey.split(",") : [],
    );
  }, [publishedMapLevelIdsKey]);

  const toggleSchoolBoundaryType = useCallback((type: string) => {
    setSchoolBoundariesHiddenTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }, []);

  const toggleSchoolPinType = useCallback((type: string) => {
    setSchoolPinsHiddenTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }, []);

  const toggleMapLevelId = useCallback((id: string) => {
    setMapLevelsHiddenIds((prev) =>
      prev.includes(id) ? prev.filter((hiddenId) => hiddenId !== id) : [...prev, id],
    );
  }, []);

  const value = useMemo<FindMySchoolLayersContextValue>(
    () => ({
      districtVisible: hasDistrictLayer ? districtVisible : false,
      setDistrictVisible,
      schoolBoundariesHiddenTypes,
      toggleSchoolBoundaryType,
      schoolPinsHiddenTypes,
      toggleSchoolPinType,
      districtSchoolLevelTypes,
      publishedMapLevels,
      mapLevelsHiddenIds,
      toggleMapLevelId,
    }),
    [
      hasDistrictLayer,
      districtVisible,
      schoolBoundariesHiddenTypes,
      toggleSchoolBoundaryType,
      schoolPinsHiddenTypes,
      toggleSchoolPinType,
      districtSchoolLevelTypes,
      publishedMapLevels,
      mapLevelsHiddenIds,
      toggleMapLevelId,
    ],
  );

  return (
    <FindMySchoolLayersContext.Provider value={value}>
      {children}
    </FindMySchoolLayersContext.Provider>
  );
}
